'use client';

// This component renders the footer of the website.
// It includes copyright information and footer links.
import Link from 'next/link';
import { useEffect, useState } from 'react';

import {
  PleyrLogo,
  TikTokIcon,
  WhiteFacebookIcon,
  WhiteInstaIcon,
} from '@/components/common/Icon';

export default function Footer() {
  const today: Date = new Date();
  const year: number = today.getFullYear();

  // Initialize path state with empty string
  const [path, setPath] = useState('');

  // Effect hook to set path on client-side
  useEffect(() => {
    // Use window.location.pathname to get the current path
    setPath(window.location.pathname);
  }, []);

  return (
    <footer className="border-t border-darkerSkyBlue bg-darkBlueBG2">
      {/* Wider-screen padding + width cap to avoid huge negative space */}
      {/* Increased horizontal padding on wide screens and a generous max width */}
      <div className="container mx-auto px-6 sm:px-8 md:px-10 lg:px-14 xl:px-24 2xl:px-32 py-8 max-w-screen-2xl">
        {/* ===== Top bar ===== */}
        {/* Mobile: logo on its own row above links */}
        {/* Desktop: logo shares the row; links are centered across the row */}
        <div className="mb-6">
          {/* Mobile logo row */}
          <div className="flex justify-center md:hidden mb-6">
            <Link href="/" className="block">
              <PleyrLogo className="w-16 h-auto" />
            </Link>
          </div>

          {/* Link rows */}
          {/* Mobile:
                Row 1 → Home, About us
                Row 2 → Privacy Policy, Terms of Use
              Desktop (md+):
                Single row with:
                  Left   → [Logo] (with comfy left padding via container)
                  Center → [Home, About us | Privacy Policy, Terms of Use] (centered, ONE separator)
                  Right  → (spacer to keep the center truly centered)
           */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-center">
            {/* Desktop left: Logo (shares the row) */}
            <div className="hidden md:flex md:items-center">
              <Link href="/" className="block">
                <PleyrLogo className="w-16 h-auto md:w-20" />
              </Link>
            </div>

            {/* Desktop center: all links centered with a single separator between groups */}
            <nav className="hidden md:flex justify-center items-center text-sm">
              {/* Group A */}
              <div className="flex items-center gap-x-4">
                <Link
                  href="/"
                  className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                    path === '/'
                      ? 'text-shadow_blue'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  Home
                </Link>
                <Link
                  href="/about"
                  className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                    path === '/about'
                      ? 'text-shadow_blue'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  About us
                </Link>
                <Link
                  href="/organizations"
                  className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                    path === '/organizations'
                      ? 'text-shadow_blue'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  For organizations
                </Link>
                <Link
                  href="/blog?page=1"
                  className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                    path.startsWith('/blog')
                      ? 'text-shadow_blue'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  Blog
                </Link>
              </div>

              {/* ONE separator */}
              <span
                aria-hidden="true"
                className="mx-5 text-white/30 select-none"
              >
                |
              </span>

              {/* Group B */}
              <div className="flex items-center gap-x-4">
                <Link
                  href="/privacy-policy"
                  className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                    path === '/privacy-policy'
                      ? 'text-shadow_blue'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  Privacy Policy
                </Link>
                <Link
                  href="/terms-of-use"
                  className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                    path === '/terms-of-use'
                      ? 'text-shadow_blue'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  Terms of Use
                </Link>
              </div>
            </nav>

            {/* Desktop right spacer to keep center truly centered */}
            <div className="hidden md:block" />

            {/* Mobile row 1: Primary nav (centered, no separators) */}
            <nav className="md:hidden flex gap-x-6 text-sm justify-center">
              <Link
                href="/"
                className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                  path === '/'
                    ? 'text-shadow_blue'
                    : 'opacity-70 hover:opacity-100'
                }`}
              >
                Home
              </Link>
              <Link
                href="/about"
                className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                  path === '/about'
                    ? 'text-shadow_blue'
                    : 'opacity-70 hover:opacity-100'
                }`}
              >
                About us
              </Link>
              <Link
                href="/organizations"
                className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                  path === '/organizations'
                    ? 'text-shadow_blue'
                    : 'opacity-70 hover:opacity-100'
                }`}
              >
                For organizations
              </Link>
              <Link
                href="/blog?page=1"
                className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                  path.startsWith('/blog')
                    ? 'text-shadow_blue'
                    : 'opacity-70 hover:opacity-100'
                }`}
              >
                Blog
              </Link>
            </nav>

            {/* Mobile row 2: Legal links (centered) */}
            <nav className="md:hidden flex gap-x-6 text-sm justify-center">
              <Link
                href="/privacy-policy"
                className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                  path === '/privacy-policy'
                    ? 'text-shadow_blue'
                    : 'opacity-70 hover:opacity-100'
                }`}
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms-of-use"
                className={`text-white font-normal font-Segoe transition-all duration-300 hover:text-shadow_blue whitespace-nowrap ${
                  path === '/terms-of-use'
                    ? 'text-shadow_blue'
                    : 'opacity-70 hover:opacity-100'
                }`}
              >
                Terms of Use
              </Link>
            </nav>
          </div>
        </div>

        {/* ===== Social Icons (always below the top bar, centered) ===== */}
        <div className="flex justify-center gap-4 mb-6">
          <Link
            aria-label="Tik-Tok"
            className="opacity-70 hover:opacity-100 hover:-translate-y-1 transition duration-300"
            href="https://www.tiktok.com/@pleyr_"
            target="_blank"
            rel="noopener noreferrer"
          >
            <TikTokIcon />
          </Link>
          <Link
            aria-label="instagram"
            className="opacity-70 hover:opacity-100 hover:-translate-y-1 transition duration-300"
            href="https://www.instagram.com/pleyr_official/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhiteInstaIcon />
          </Link>
          <Link
            aria-label="facebook"
            className="opacity-70 hover:opacity-100 hover:-translate-y-1 transition duration-300"
            href="https://www.facebook.com/profile.php?id=61582556600665"
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhiteFacebookIcon />
          </Link>
        </div>

        {/* Divider */}
        {/* <div className="border-t border-partnersBorders opacity-20 mb-4"></div> */}

        {/* Copyright */}
        <div className="text-center">
          <p className="text-white text-xs font-Segoe opacity-50">
            &copy;{year} Pleyr. All rights reserved
          </p>
        </div>
      </div>
    </footer>
  );
}
